import {useEffect} from "react";

import {useQueryClient} from "@tanstack/react-query";

import {
  invalidateAllDirectMessageThreadLists,
  invalidateNotificationFeed,
  refetchDirectMessageThread,
} from "../lib/direct-message-query-cache";

const DM_POST_CREATED_EVENTS = [
  "directmessage:post-created",
  ".directmessage:post-created",
] as const;

const DM_REPLIES_TOGGLED_EVENTS = [
  "directmessage:replies-toggled",
  ".directmessage:replies-toggled",
] as const;

const DM_READ_EVENTS = ["directmessage:read", ".directmessage:read"] as const;

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function getThreadIdFromPayload(payload: unknown): number | null {
  const record = asRecord(payload);
  const threadId = record?.direct_message_thread_id;
  return typeof threadId === "number" ? threadId : null;
}

export function useDirectMessagesRealtime() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const refresh = (payload: unknown) => {
      const threadId = getThreadIdFromPayload(payload);
      invalidateAllDirectMessageThreadLists(queryClient);

      if (threadId != null) {
        refetchDirectMessageThread(queryClient, threadId);
      }

      invalidateNotificationFeed(queryClient);
    };

    const onPostCreated = (event: Event) => {
      refresh((event as CustomEvent<unknown>).detail);
    };

    const onRepliesToggled = (event: Event) => {
      refresh((event as CustomEvent<unknown>).detail);
    };

    const onRead = (event: Event) => {
      refresh((event as CustomEvent<unknown>).detail);
    };

    const bindings: Array<[readonly string[], (event: Event) => void]> = [
      [DM_POST_CREATED_EVENTS, onPostCreated],
      [DM_REPLIES_TOGGLED_EVENTS, onRepliesToggled],
      [DM_READ_EVENTS, onRead],
    ];

    for (const [events, handler] of bindings) {
      events.forEach((eventName) => window.addEventListener(eventName, handler));
    }

    return () => {
      for (const [events, handler] of bindings) {
        events.forEach((eventName) => window.removeEventListener(eventName, handler));
      }
    };
  }, [queryClient]);
}
